import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
} from "@mui/material";
import Icon from "@mdi/react";
import { mdiClose, mdiPuzzleOutline } from "@mdi/js";
import styles from "./styles.module.scss";
import { clsx } from "@ismael1361/utils";
import { NotistackServices, WorkspaceServices, PublicWorkspaceDTO, PublicTeamMemberDTO } from "shared/services";
import { PublicMcpDTO, SeniorityLevel } from "shared/services/McpLibraryServices";
import { HandleError } from "shared/utils";

interface HireMemberModalProps {
	open: boolean;
	mcp?: PublicMcpDTO | null;
	onClose: () => void;
	onHired: (member: PublicTeamMemberDTO) => void;
}

const SENIORITY_META: Record<SeniorityLevel, { label: string; description: string }> = {
	junior: { label: "Júnior", description: "Modelos rápidos, tarefas simples e conservadoras" },
	pleno: { label: "Pleno", description: "Equilíbrio entre custo, velocidade e qualidade" },
	senior: { label: "Sênior", description: "Modelos avançados, máxima autonomia e criatividade" },
};

const SENIORITY_ORDER: SeniorityLevel[] = ["junior", "pleno", "senior"];

function formatPrice(value?: number): string {
	if (typeof value !== "number" || value <= 0) return "—";
	return `R$ ${value.toLocaleString("pt-BR", { minimumFractionDigits: 0 })}/mês`;
}

export const HireMemberModal: React.FC<HireMemberModalProps> = ({ open, mcp, onClose, onHired }) => {
	const [workspaces, setWorkspaces] = useState<PublicWorkspaceDTO[]>([]);
	const [selectedWorkspaceUid, setSelectedWorkspaceUid] = useState<string>("");
	const [selectedSeniority, setSelectedSeniority] = useState<SeniorityLevel>("pleno");
	const [loading, setLoading] = useState(false);
	const [loadingWorkspaces, setLoadingWorkspaces] = useState(true);

	useEffect(() => {
		(async () => {
			try {
				const data = await WorkspaceServices.listWorkspaces();
				setWorkspaces(data.filter((t) => t.isActive));
				if (data.length > 0) {
					setSelectedWorkspaceUid(data[0].uid);
				}
			} catch {
				NotistackServices.showErrorMessage("Erro ao carregar workspaces");
			} finally {
				setLoadingWorkspaces(false);
			}
		})();
	}, []);

	const availableSeniorities = useMemo(() => {
		return SENIORITY_ORDER;
	}, []);

	useEffect(() => {
		if (availableSeniorities.length > 0 && !availableSeniorities.includes(selectedSeniority)) {
			setSelectedSeniority(availableSeniorities[0]);
		}
	}, [availableSeniorities, selectedSeniority]);

	const handleHire = useCallback(async () => {
		if (!selectedWorkspaceUid) {
			NotistackServices.showWarningMessage("Selecione um workspace");
			return;
		}
		if (!mcp) {
			NotistackServices.showWarningMessage("Selecione um MCP");
			return;
		}
		setLoading(true);
		try {
			const member = await WorkspaceServices.hireMember(selectedWorkspaceUid, {
				mcp_id: mcp.id,
				seniority: selectedSeniority,
			});
			NotistackServices.showSuccessMessage(`${member.name} contratado(a) com sucesso!`);
			onHired(member);
			onClose();
		} catch (error: any) {
			const message = HandleError.isHandleError(error) ? error.message : "Erro ao contratar membro";
			NotistackServices.showErrorMessage(message);
		} finally {
			setLoading(false);
		}
	}, [selectedWorkspaceUid, selectedSeniority, mcp?.id, onHired, onClose]);

	const handleOverlayClick = (e: React.MouseEvent) => {
		if (e.target === e.currentTarget) onClose();
	};

	return (
		<Dialog
			open={open && !!mcp}
			onClose={onClose}
			scroll={"paper"}
		>
			{/* Header */}
			<DialogTitle>Contratar Membro</DialogTitle>

			<DialogContent dividers>
				{/* Profile */}
				<div className={styles.profileSection}>
					<div className={styles.avatar}>
						{mcp?.iconUrl ? (
							<img
								src={mcp?.iconUrl}
								alt={mcp?.name}
							/>
						) : (
							<Icon
								path={mdiPuzzleOutline}
								size={1.5}
								color="#555"
							/>
						)}
					</div>
					<div className={styles.profileInfo}>
						<p className={styles.profileName}>{mcp?.name}</p>
						<p className={styles.profileCategory}>{mcp?.description || mcp?.category}</p>
						<div className={styles.profileCompetencies}>
							{mcp?.deliverableTypes?.slice(0, 5).map((dt, i) => (
								<span
									key={i}
									className={styles.competencyTag}
								>
									{dt}
								</span>
							))}
						</div>
					</div>
				</div>

				{/* Seniority Selection */}
				<div className={styles.senioritySection}>
					<p className={styles.sectionLabel}>Escolha a Senioridade</p>
					<div className={styles.seniorityOptions}>
						{SENIORITY_ORDER.map((level) => {
							const available = true;
							const price = mcp?.pricingModel?.[level];
							const meta = SENIORITY_META[level];

							return (
								<div
									key={level}
									className={clsx(
										styles.seniorityOption,
										selectedSeniority === level && styles.selected,
										!available && styles.disabled,
									)}
									onClick={() => available && setSelectedSeniority(level)}
								>
									<span className={clsx(styles.seniorityBadge, styles[level])}>{meta.label}</span>
									<div className={styles.seniorityInfo}>
										<div className={styles.seniorityLabel}>{meta.label}</div>
										<div className={styles.seniorityDesc}>{meta.description}</div>
									</div>
									<span className={styles.seniorityPrice}>{formatPrice(price)}</span>
								</div>
							);
						})}
					</div>
				</div>

				{/* Team Selection */}
				<div className={styles.teamSection}>
					<p className={styles.sectionLabel}>Workspace de Destino</p>
					{loadingWorkspaces ? (
						<CircularProgress
							color="inherit"
							size={20}
						/>
					) : workspaces.length === 0 ? (
						<p style={{ color: "#b3b3b3", fontSize: "0.875rem" }}>Nenhum workspace disponível. Crie um primeiro.</p>
					) : (
						<FormControl
							fullWidth
							size="small"
						>
							<InputLabel id="team-select-label">Workspace</InputLabel>
							<Select
								labelId="team-select-label"
								value={selectedWorkspaceUid}
								label="Workspace"
								onChange={(e) => setSelectedWorkspaceUid(e.target.value)}
							>
								{workspaces.map((ws) => (
									<MenuItem
										key={ws.uid}
										value={ws.uid}
									>
										{ws.name} {ws.membersCount != null ? `(${ws.membersCount}/4 membros)` : ""}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					)}
				</div>
			</DialogContent>

			{/* Footer */}
			<DialogActions>
				<Button
					variant="text"
					onClick={onClose}
					disabled={loading}
					sx={{ color: "#b3b3b3", textTransform: "none" }}
				>
					Cancelar
				</Button>
				<Button
					variant="contained"
					onClick={handleHire}
					disabled={loading || !selectedWorkspaceUid || loadingWorkspaces}
					sx={{ textTransform: "none", fontWeight: 700 }}
				>
					{loading ? (
						<CircularProgress
							size={20}
							color="inherit"
						/>
					) : (
						"Contratar"
					)}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
